import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { IntlProvider } from 'react-intl';
import { CustomProvider } from 'rsuite';
import frFR from 'rsuite/locales/fr_FR';
import { setupResponseInterceptor } from './api/api-commons';
import DataSourcesDisplayer from './components/devices/DataSources';
import DeviceList from './pages/management/devices/manualDevices/DeviceList';
import ManagementHome from './pages/management/home/ManagementHome';
import MetadataDisplayer from './pages/management/MetadataDisplayer';
import ModelsDisplayer from './pages/management/ModelsDisplayer';
import ProjectDisplayer from './pages/management/projects/ProjectList';
import SiteDisplayer from './pages/management/sites/SiteList';
import User from './pages/management/users/User';
import UserDisplayer from './pages/management/users/UserList';
import ErrorBoundary from './pages/security/ErrorBoundary';
import LogoutComponent from './pages/security/Logout';

import clearThemeOptions from './assets/mui-themes/clear-theme';
import { SnackbarRenderer } from './components/utils/MUISpecifics/snackbar/SnackbarRenderer';
import SiteDashboardRoute from './dashboard/SiteDashboardRoute';
import StandaloneDashboardRoute from './dashboard/StandaloneDashboardRoute';
import Home from './pages/home/Home';
import FiltredStations from './pages/management/stations/FiltredStations';
import Station from './pages/management/stations/Station';
import AlertView from './pages/sites/alert-view/AlertView';
import SiteCampaignsComponent from './pages/sites/campaign-view/SiteCampaignsComponent';
import MeasureDataComponent from './pages/sites/manual-measure-view/MeasureDataComponent';
import SiteReportRoute from './pages/sites/report-view/SiteReportRoute';
import { useDocumentTitle } from './utils/hooks/useDocumentTitle';
import { useHomePage } from './utils/hooks/useHomePage';

// TODO: move to useEffect of component?
const withDispatchResize = (render: () => JSX.Element) => {
  setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  return render();
};

const dashboardQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

export default function App() {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState<boolean>(false);
  const homePageState = useHomePage();

  useDocumentTitle('Démonstrateur - High Impact', 'global');

  if (!loaded) {
    setLoaded(true);
    setupResponseInterceptor(navigate);
  }

  // Listen to navigation events from micro-frontends and navigate using react-router-dom
  // Every mfe can and must trigger an "mfe:navigate" event with the url to navigate to in the detail.
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      navigate(e.detail.url);
    };
    window.addEventListener('mfe:navigate', handler as EventListener);
    return () =>
      window.removeEventListener('mfe:navigate', handler as EventListener);
  }, [navigate]);

  return (
    <QueryClientProvider client={dashboardQueryClient}>
      <IntlProvider locale="fr-FR">
        <SnackbarRenderer />
        <CustomProvider locale={frFR}>
          <ThemeProvider theme={clearThemeOptions}>
            <ErrorBoundary>
              <Routes>
                {/**App navigation */}
                <Route path="/" element={<Navigate to="/home" />} />
                <Route path="/logout" element={<LogoutComponent />} />
                <Route
                  path="/home"
                  element={
                    homePageState.status === 'component' ? (
                      <homePageState.Component />
                    ) : homePageState.status === 'loading' ? (
                      <h4>Chargement...</h4>
                    ) : (
                      // Fallback to default home
                      <Home />
                    )
                  }
                />

                {/**
                 * Paths to backoffice pages.
                 */}
                <Route path="/management" element={<ManagementHome />} />
                <Route
                  path="/management/projects"
                  element={<ProjectDisplayer />}
                />
                <Route path="/management/sites" element={<SiteDisplayer />} />
                <Route path="/management/users" element={<UserDisplayer />} />
                <Route
                  path="/management/datasources"
                  element={<DataSourcesDisplayer />}
                />

                {/**
                 * Paths to detail pages.
                 */}
                {/* <Route path="/management/sites/:site_id/" element={<Site />} /> */}

                <Route
                  path="/management/sites/:site_id/stations"
                  element={<FiltredStations />}
                />

                {/* <Route path="/management/projects/:project_id" element={<Project />}/> */}

                <Route path="/management/users/:user_id" element={<User />} />

                {/**Project navigation */}
                <Route
                  path="/management/projects/:project_id/project_general_infos"
                  element={<MetadataDisplayer />}
                />
                <Route
                  path="/management/projects/:project_id/project_models"
                  element={<ModelsDisplayer />}
                />

                {/**Project navigation */}
                <Route
                  path="/management/sites/:site_id/site_general_infos"
                  element={<MetadataDisplayer />}
                />

                {/**Devices navigation */}
                <Route path="/management/devices" element={<DeviceList />} />
                {/* <Route
                path="/management/devices/:device_id"
                element={<Device />}
              /> */}
                {/**Main pages */}
                <Route
                  path="/home/dashboards/:grid_id"
                  element={<StandaloneDashboardRoute />}
                />
                <Route
                  path="/projects/:project_id/sites/:site_id/tableau-de-bord/:grid_id"
                  element={withDispatchResize(() => (
                    <SiteDashboardRoute />
                  ))}
                />
                <Route
                  path="/public/projects/:project_id/sites/:site_id/tableau-de-bord/:grid_id"
                  element={withDispatchResize(() => (
                    <SiteDashboardRoute />
                  ))}
                />
                <Route
                  path="/projects/:project_id/sites/:site_id/stations-de-mesures"
                  element={<FiltredStations />}
                />
                <Route
                  path="/projects/:project_id/sites/:site_id/stations-de-mesures/:station_id"
                  element={<Station />}
                />
                <Route
                  path="/projects/:project_id/sites/:site_id/campagnes"
                  element={<SiteCampaignsComponent />}
                />
                <Route
                  path="/projects/:project_id/sites/:site_id/données-de-mesures"
                  element={<MeasureDataComponent />}
                />
                <Route
                  path="/projects/:project_id/sites/:site_id/rapports/:grid_id"
                  element={withDispatchResize(() => (
                    <SiteReportRoute />
                  ))}
                />
                <Route
                  path="/projects/:project_id/sites/:site_id/alertes"
                  element={withDispatchResize(() => (
                    <AlertView />
                  ))}
                />
                {/* <Route path="/management/data" element = {<AddDataForm/>} /> */}
              </Routes>
            </ErrorBoundary>
          </ThemeProvider>
        </CustomProvider>
      </IntlProvider>
    </QueryClientProvider>
  );
}
